import { useState } from 'react';
import { History, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, IconButton, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { EmptyState, Timeline, type TimelineItem } from '@vnetwork/ui/data';
import { useTicketLogs } from '@/api/tickets';
import { formatDateTime } from '@/lib/format';

const PAGE_SIZE = 10;

interface LogsPanelProps {
  ticketId: number;
}

/**
 * Paginated activity log panel. Used in the sidebar on md+ and as a tab on
 * small screens. Fetches in pages of 10; shows prev/next controls.
 */
export function LogsPanel({ ticketId }: LogsPanelProps) {
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = useTicketLogs(ticketId, {
    page,
    page_size: PAGE_SIZE,
  });

  const logs = data?.results ?? [];
  const pagination = data?.pagination;
  const totalPages = pagination?.total_pages ?? 1;

  if (isLoading) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-2)', padding: 'var(--vn-space-3)' }}>
        {[1, 2, 3, 4].map((i) => <Skeleton key={i} style={{ height: 40 }} />)}
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: 'var(--vn-space-3)' }}>
        <Alert
          tone="danger"
          title="Failed to load activity log"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 0 }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--vn-space-2)',
          padding: 'var(--vn-space-3)',
          borderBottom: '1px solid var(--vn-border-default)',
        }}
      >
        <History size={14} style={{ color: 'var(--vn-fg-muted)' }} />
        <span
          style={{
            fontSize: 'var(--vn-font-size-sm)',
            fontWeight: 'var(--vn-font-weight-medium)',
          }}
        >
          Activity Log
        </span>
        {pagination?.total != null && (
          <Badge size="sm" tone="neutral" style={{ marginLeft: 'auto' }}>
            {pagination.total}
          </Badge>
        )}
      </div>

      {/* Log entries */}
      {logs.length === 0 ? (
        <div style={{ padding: 'var(--vn-space-4)' }}>
          <EmptyState
            icon={<History size={28} />}
            title="No activity yet"
            description="Actions on this ticket will appear here."
          />
        </div>
      ) : (
        <div style={{ padding: 'var(--vn-space-3)' }}>
          <Timeline
            size="sm"
            items={logs.map((log, i): TimelineItem => ({
              id: i,
              title: log.employee_name || 'System',
              timestamp: formatDateTime(log.created_at),
              description: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)', flexWrap: 'wrap' }}>
                  <Badge size="sm" tone="neutral" style={{ textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                    {log.action}
                  </Badge>
                  {log.description && (
                    <span style={{ color: 'var(--vn-fg-default)', fontSize: 'var(--vn-font-size-xs)' }}>
                      {log.description}
                    </span>
                  )}
                </span>
              ),
            }))}
          />
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--vn-space-2) var(--vn-space-3)',
            borderTop: '1px solid var(--vn-border-default)',
          }}
        >
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft size={14} />
          </IconButton>
          <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
            {page} / {totalPages}
          </span>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Next page"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            <ChevronRight size={14} />
          </IconButton>
        </div>
      )}
    </div>
  );
}
